import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { SearchItem, SearchPaletteRegistryService } from './search-palette-registry.service';

export type { SearchItem } from './search-palette-registry.service';

@Component({
    selector: 'app-search-palette',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (isOpen()) {
            <div class="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" (click)="onBackdropClick($event)">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

                <!-- Palette -->
                <div
                    class="relative w-full max-w-[600px] mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl 
                    overflow-hidden border
                     border-gray-200 dark:border-gray-700">
                    <!-- Search Input -->
                    <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                        </svg>
                        <input
                            #searchInput
                            autocomplete="off"
                            class="flex-1 bg-transparent text-gray-800 dark:text-white placeholder-gray-400
                             dark:placeholder-gray-500 text-sm outline-none"
                            placeholder="Rechercher une page..."
                            type="text"
                            [value]="query()"
                            (input)="onQueryChange($event)"
                            (keydown)="onKeyDown($event)" />
                        <kbd
                            class="hidden sm:inline-flex items-center px-1.5 py-0.5 text-xs font-mono bg-gray-100
                             dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded border border-gray-200 dark:border-gray-600">
                            ESC
                        </kbd>
                    </div>

                    <!-- Results -->
                    <div class="max-h-[320px] overflow-y-auto">
                        @if (filteredResults().length > 0) {
                            <ul class="py-1">
                                @for (item of filteredResults(); track item.url; let i = $index) {
                                    <li>
                                        <button
                                            class="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors"
                                            type="button"
                                            [class]="
                                                i === selectedIndex()
                                                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 
                                                    dark:hover:bg-gray-700/50'
                                            "
                                            (click)="selectItem(item)"
                                            (mouseenter)="selectedIndex.set(i)">
                                            <div
                                                class="w-8 h-8 rounded-lg flex items-center justify-center 
                                                flex-shrink-0"
                                                [class]="
                                                    i === selectedIndex()
                                                        ? 'bg-blue-100 dark:bg-blue-800/50 text-blue-600 dark:text-blue-400'
                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                                ">
                                                <svg
                                                    class="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    [innerHTML]="item.icon"></svg>
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <p class="font-medium truncate">{{ item.title }}</p>
                                                <p
                                                    class="text-xs truncate"
                                                    [class]="
                                                        i === selectedIndex()
                                                            ? 'text-blue-500/70 dark:text-blue-400/70'
                                                            : 'text-gray-400 dark:text-gray-500'
                                                    ">
                                                    {{ item.subtitle }}
                                                </p>
                                            </div>
                                            @if (i === selectedIndex()) {
                                                <svg
                                                    class="w-4 h-4 flex-shrink-0 text-blue-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                                </svg>
                                            }
                                        </button>
                                    </li>
                                }
                            </ul>
                        } @else if (query().length > 0) {
                            <div class="py-8 text-center">
                                <svg class="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="1.5" />
                                </svg>
                                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">Aucun résultat pour "{{ query() }}"</p>
                            </div>
                        }
                    </div>

                    <!-- Footer -->
                    <div
                        class="border-t border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between text-xs text-gray-400
                         dark:text-gray-500">
                        <div class="flex items-center gap-3">
                            <span class="flex items-center gap-1">
                                <kbd class="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs border border-gray-200 dark:border-gray-600"
                                    >&uarr;</kbd
                                >
                                <kbd class="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs border border-gray-200 dark:border-gray-600"
                                    >&darr;</kbd
                                >
                                naviguer
                            </span>
                            <span class="flex items-center gap-1">
                                <kbd class="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs border border-gray-200 dark:border-gray-600"
                                    >Enter</kbd
                                >
                                ouvrir
                            </span>
                            <span class="flex items-center gap-1">
                                <kbd class="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs border border-gray-200 dark:border-gray-600"
                                    >Esc</kbd
                                >
                                fermer
                            </span>
                        </div>
                        <span>SISPEV01</span>
                    </div>
                </div>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: contents;
            }
        `,
    ],
    host: {
        '(document:keydown)': 'onGlobalKeyDown($event)',
    },
})
export class SearchPaletteComponent {
    private router = inject(Router);
    private elementRef = inject(ElementRef);
    private registry = inject(SearchPaletteRegistryService);

    /**
     * Items à afficher dans la palette. Si non fourni, le composant lit depuis
     * SearchPaletteRegistryService (items par défaut + tout ce que les modules
     * ont enregistré dynamiquement). Override possible page par page.
     */
    readonly items = input<SearchItem[] | null>(null);

    /** Raccourci clavier d'ouverture. Défaut : Ctrl+K / Cmd+K. */
    readonly hotkey = input<string>('k');

    /** Limite d'items affichés simultanément (résultats filtrés ou liste par défaut). */
    readonly maxResults = input<number>(8);

    readonly isOpen = signal(false);
    readonly query = signal('');
    readonly selectedIndex = signal(0);

    /** Source effective : input prioritaire, sinon registre global. */
    private readonly sourceItems = computed<SearchItem[]>(() => this.items() ?? this.registry.items());

    readonly filteredResults = computed(() => {
        const items = this.sourceItems();
        const q = this.query().toLowerCase().trim();
        const limit = this.maxResults();

        if (!q) {
            return items.slice(0, limit);
        }

        return items.filter((item) => item.title.toLowerCase().includes(q) || item.subtitle.toLowerCase().includes(q)).slice(0, limit);
    });

    onGlobalKeyDown(event: KeyboardEvent): void {
        if ((event.ctrlKey || event.metaKey) && event.key === this.hotkey()) {
            event.preventDefault();
            this.toggle();
        }
    }

    toggle(): void {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open();
        }
    }

    open(): void {
        this.isOpen.set(true);
        this.query.set('');
        this.selectedIndex.set(0);
        setTimeout(() => {
            const el = this.elementRef.nativeElement.querySelector('input');

            el?.focus();
        }, 50);
    }

    close(): void {
        this.isOpen.set(false);
        this.query.set('');
        this.selectedIndex.set(0);
    }

    onQueryChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;

        this.query.set(value);
        this.selectedIndex.set(0);
    }

    onKeyDown(event: KeyboardEvent): void {
        const results = this.filteredResults();

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.selectedIndex.update((i) => (i + 1) % results.length);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.selectedIndex.update((i) => (i - 1 + results.length) % results.length);
                break;
            case 'Enter':
                event.preventDefault();
                if (results.length > 0) {
                    this.selectItem(results[this.selectedIndex()]);
                }
                break;
            case 'Escape':
                event.preventDefault();
                this.close();
                break;
        }
    }

    selectItem(item: SearchItem): void {
        this.router.navigate([item.url]);
        this.close();
    }

    onBackdropClick(event: MouseEvent): void {
        if ((event.target as HTMLElement).classList.contains('fixed')) {
            this.close();
        }
    }
}
